// 全屏「准备文件」：新建任务（含测试 / 探索任务）起名后先到这里，
// 文件传齐、必填字段都有值才能进入任务界面开始运行。
import { AssigneePicker } from "../components/AssigneePicker";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errorText } from "../lib/api";
import { useInterval, useLoad } from "../lib/hooks";
import { useWsMessages } from "../lib/realtime";
import { FileBoard, focusMissing } from "../components/FileBoard";
import { ExtractionPanel } from "../components/ExtractionPanel";

export function PreparePage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const taskQ = useLoad(() => api.task(id), [id]);
  const task = taskQ.data;
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (task) { setTitle(task.title); setNote(task.materialsNote); } }, [task?.id]);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void taskQ.reload(), 250);
  }, [taskQ]);
  useWsMessages((m) => { if (m.type === "task" && m.taskId === id) refresh(); });
  // 归类 / 提取在后台进行：进行中每 4 秒兜底刷新一次
  const busyBackend = !!task && (task.extraction?.status === "running" || task.files.some((f) => f.state !== "synced" && f.state !== "failed"));
  useInterval(refresh, busyBackend ? 4000 : null);

  if (taskQ.error && !task) return <div className="prep-page"><div className="notice err">{taskQ.error}</div></div>;
  if (!task) return <div className="prep-page"><div className="empty">加载中…</div></div>;

  const editable = ["draft", "ready", "failed"].includes(task.status);
  const save = async () => {
    setSaving(true); setError(null);
    try {
      await api.updateTask(task.id, { title: title.trim() || task.title, materialsNote: note.trim(), expectedUpdatedAt: task.updatedAt });
      await taskQ.reload();
    } catch (e) { setError(errorText(e)); }
    finally { setSaving(false); }
  };
  const dirty = title.trim() !== task.title || note.trim() !== task.materialsNote.trim();
  const noPerson = task.browserKind === "corppass" && !task.assignee;
  const ready = task.status === "ready" && !task.missing.length && !busyBackend && !noPerson;
  const enter = async () => {
    if (dirty) await save();
    navigate(`/tasks/${task.id}`);
  };

  return (
    <div className="prep-page">
      <div className="prep-head">
        <Link className="btn" to="/">← 任务队列</Link>
        <span className="prep-step small muted">第 1 步 · 准备文件 · #{task.no}{task.learningRun ? (task.dryRun ? " · 探索（演练）" : " · 正式探索") : ""}</span>
        <input className="prep-title" aria-label="任务名称" title="任务名称（可改）" value={title} maxLength={200} disabled={!editable || saving} onChange={(e) => setTitle(e.target.value)} onBlur={() => { if (dirty) void save(); }} />
        <AssigneePicker task={task} onChanged={refresh} />
        {editable && <Link className="btn" to={`/tasks/${task.id}/edit`}>✎ 用表单填写资料</Link>}
        {!editable && <Link className="btn" to={`/tasks/${task.id}`}>查看任务</Link>}
      </div>

      <FileBoard task={task} canUpload={editable} onChanged={refresh} extraTabs={[{
        key: "extraction",
        label: "信息提取",
        tone: task.extraction?.status === "running" ? "warn" : task.extraction?.status === "failed" ? "bad" : task.extraction?.status === "done" ? "ok" : "idle",
        content: <ExtractionPanel task={task} canEdit={editable} onChanged={refresh} />,
      }]} noteCard={
        <div className={`fb-block fb-${note.trim() ? "ok" : "idle"}`} data-testid="note-card">
          <b>补充说明</b>
          <p className="small muted" style={{ margin: "4px 0" }}>材料里没有的信息写在这里，系统会一起提取，例如担保人邮箱 / 手机、是否被拒入境等。</p>
          <textarea aria-label="补充说明" value={note} maxLength={2000} disabled={!editable || saving} onChange={(e) => setNote(e.target.value)} rows={6} style={{ width: "100%", boxSizing: "border-box" }} />
          <div className="row end" style={{ marginTop: 6 }}>
            <button className="btn btn-sm" disabled={!dirty || saving} onClick={() => void save()}>{saving ? "保存中…" : "保存并重新提取"}</button>
          </div>
        </div>
      } />
      <div className="prep-foot">
        {error && <span className="err">{error}</span>}
        {busyBackend ? <span className="muted">正在归类 / 提取，请稍候…</span>
          : task.missing.length ? <span className="err" data-testid="prep-missing">还缺 {task.missing.length} 项（点一项去改）：{task.missing.map((m, i) => <span key={m}>{i ? "、" : ""}<button type="button" className="link-btn" onClick={() => focusMissing(m)}>{m}</button></span>)}</span>
          : <span className="ok-text">文件和字段都齐了</span>}
        <span className="spacer" />
        <button className="btn btn-primary btn-big" data-testid="prep-enter" disabled={!ready || saving} title={ready ? undefined : noPerson ? "先在顶上选谁来扫码登录 Corppass" : "文件和必填字段都齐了才能进入"} onClick={() => void enter()}>{ready ? "准备好了，进入任务 →" : "补齐后才能进入任务"}</button>
      </div>
    </div>
  );
}
